import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import ConnectionHandler from "@sendbird/uikit-react/handlers/ConnectionHandler";
import { fetchTicketInfo } from "../../api/agent/fetchTicketInfo";
import { uuidv4 } from "../../../../utils/uuid/uuidv4";

const ConnectionStateContext = createContext({
  isInputEnabled: true,
  isReconnecting: false,
  isSyncing: false,
});

export const useConnectionState = () => useContext(ConnectionStateContext);

const isSdkOpen = (sdk) => sdk?.connectionState === "OPEN";

const isTicketClosed = (ticketData) => ticketData?.status2 === "CLOSED";

export const ConnectionStateProvider = ({
  channelUrl,
  ticketId,
  ticketClose = false,
  onTicketClosed,
  children,
}) => {
  const [isInputEnabled, setIsInputEnabled] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const channelUrlRef = useRef(channelUrl);
  const ticketIdRef = useRef(ticketId);
  const ticketCloseRef = useRef(ticketClose);
  const onTicketClosedRef = useRef(onTicketClosed);
  const peerIp = useSelector((state) => state.peerIp.ip);
  const peerIpRef = useRef(peerIp);

  useEffect(() => {
    channelUrlRef.current = channelUrl;
  }, [channelUrl]);

  useEffect(() => {
    ticketIdRef.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    ticketCloseRef.current = ticketClose;
  }, [ticketClose]);

  useEffect(() => {
    onTicketClosedRef.current = onTicketClosed;
  }, [onTicketClosed]);

  useEffect(() => {
    peerIpRef.current = peerIp;
  }, [peerIp]);

  const { stores } = useSendbirdStateContext();
  const sdk = stores?.sdkStore?.sdk;

  const refreshChannel = useCallback(async () => {
    if (!sdk || !channelUrlRef.current) {
      return;
    }

    const channel = await sdk.groupChannel.getChannel(channelUrlRef.current);
    await channel.refresh();
  }, [sdk]);

  const syncTicketStatus = useCallback(async () => {
    if (ticketCloseRef.current || !ticketIdRef.current) {
      return;
    }

    setIsSyncing(true);

    try {
      await Promise.all([
        refreshChannel().catch(() => {}),
        fetchTicketInfo(ticketIdRef.current, peerIpRef.current)
          .then((response) => {
            if (
              response?.code === "0000" &&
              isTicketClosed(response.data) &&
              !ticketCloseRef.current
            ) {
              onTicketClosedRef.current?.(response.data);
            }
          })
          .catch(() => {}),
      ]);
    } finally {
      setIsSyncing(false);
    }
  }, [refreshChannel]);

  useEffect(() => {
    if (!sdk) {
      return;
    }

    setIsInputEnabled(isSdkOpen(sdk));
    setIsReconnecting(sdk.connectionState === "CONNECTING");
  }, [sdk]);

  useEffect(() => {
    if (!sdk) {
      return;
    }

    const uniqueHandlerId = uuidv4();

    try {
      const handler = new ConnectionHandler({
        onConnected() {
          setIsInputEnabled(true);
          setIsReconnecting(false);
        },
        onDisconnected() {
          setIsInputEnabled(false);
          setIsReconnecting(false);
        },
        onReconnectStarted() {
          setIsInputEnabled(false);
          setIsReconnecting(true);
        },
        onReconnectSucceeded: async () => {
          try {
            await syncTicketStatus();
          } finally {
            if (!ticketCloseRef.current) {
              setIsInputEnabled(true);
              setIsReconnecting(false);
            }
          }
        },
        onReconnectFailed() {
          setIsInputEnabled(false);
          setIsReconnecting(true);
        },
      });

      if (typeof sdk.addConnectionHandler === "function") {
        sdk.addConnectionHandler(uniqueHandlerId, handler);
      }

      return () => {
        try {
          sdk.removeConnectionHandler(uniqueHandlerId);
        } catch {
          //
        }
      };
    } catch {
      console.error(
        "Something went wrong with the connectionHandler registration"
      );
    }
  }, [sdk, syncTicketStatus]);

  useEffect(() => {
    if (!ticketId || ticketClose) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncTicketStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [ticketId, ticketClose, syncTicketStatus]);

  return (
    <ConnectionStateContext.Provider
      value={{ isInputEnabled, isReconnecting, isSyncing }}
    >
      {children}
    </ConnectionStateContext.Provider>
  );
};

export default useConnectionState;
